/**
 * WafCustomRules.tsx
 *
 * WAF "Custom Rules" inner tab — CRUD for custom wirefilter expression rules.
 * Includes the WafRuleDialog (only used here). Self-contained: owns its own
 * API mutations and local editing state.
 */
import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, FlaskConical } from 'lucide-react';
import { SettingRow } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Textarea, Switch, Badge, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafRules, useSaveWafRule, useDeleteWafRule, useTestWafExpression,
  type WAFRule,
} from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';

const WAF_ACTIONS = ['block', 'allow', 'log', 'challenge', 'bypass'];

// ── Custom Rules list ───────────────────────────────────────────────────────
export function WafCustomRules({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const { data: rules = [], isLoading } = useWafRules(domainId);
  const [editing, setEditing] = useState<WAFRule | 'new' | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WAFRule | null>(null);

  const save = useSaveWafRule(domainId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule saved' }); setEditing(null); },
    onError: (e) => show({ tone: 'danger', title: 'Could not save rule', description: errorMessage(e, 'Could not save rule') }),
  });
  const remove = useDeleteWafRule(domainId, {
    onSuccess: () => { trackFeatureAction('cdn', 'waf_custom_rule', 'delete'); show({ tone: 'success', title: 'Rule deleted' }); setPendingDelete(null); },
    onError: (e) => show({ tone: 'danger', title: 'Could not delete rule', description: errorMessage(e, 'Could not delete rule') }),
  });

  const columns = useMemo<ColumnDef<WAFRule, unknown>[]>(() => [
    { id: 'priority', header: 'Priority', cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.priority}</Badge> },
    { id: 'rule_id', header: 'Rule ID', cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.rule_id}</span> },
    { id: 'expr', header: 'Expression', cell: ({ row }) => <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.expression}</code> },
    { id: 'action', header: 'Action', cell: ({ row }) => <Badge tone={row.original.action === 'block' ? 'danger' : 'info'} appearance="subtle" size="sm">{row.original.action}</Badge> },
    { id: 'status', header: 'Status', cell: ({ row }) => <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--vn-fg-muted)' }}>{row.original.block_status || '—'}</span> },
    { id: 'state', header: 'State', cell: ({ row }) => <StatusPill tone={row.original.enabled ? 'success' : 'muted'}>{row.original.enabled ? 'On' : 'Off'}</StatusPill> },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-1)' }} onClick={(e) => e.stopPropagation()}>
          <IconButton variant="ghost" size="sm" aria-label="Edit" onClick={() => setEditing(row.original)}><Pencil size={15} /></IconButton>
          <IconButton variant="ghost" size="sm" aria-label="Delete" onClick={() => setPendingDelete(row.original)}><Trash2 size={15} /></IconButton>
        </div>
      ),
    },
  ], []);

  return (
    <Card>
      <CardHeader actions={<Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Add rule</Button>}>
        <CardTitle>Custom rules</CardTitle><CardDescription>Custom WAF rules using wirefilter expressions.</CardDescription>
      </CardHeader>
      <CardBody style={{ padding: rules.length ? 0 : undefined }}>
        {isLoading ? <Skeleton style={{ height: 100 }} /> : rules.length === 0 ? (
          <EmptyState size="sm" title="No custom rules" description="Add a wirefilter rule to match and act on requests." />
        ) : (
          <DataTable columns={columns} data={rules} getRowId={(r) => String(r.id)} onRowClick={(r) => setEditing(r)} />
        )}
      </CardBody>

      {editing && (
        <WafRuleDialog
          domainId={domainId}
          initial={editing === 'new' ? { id: 0, rule_id: '', expression: '', action: 'block', block_status: 403, priority: 100, enabled: true } : editing}
          saving={save.isPending}
          onCancel={() => setEditing(null)}
          onSave={(r) => save.mutate(r, { onSuccess: () => trackFeatureAction('cdn', 'waf_custom_rule', r.id ? 'edit' : 'create') })}
        />
      )}
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete WAF rule?"
        description={`Remove "${pendingDelete?.rule_id}"?`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Card>
  );
}

// ── Rule editor dialog ──────────────────────────────────────────────────────
function WafRuleDialog({ domainId, initial, saving, onCancel, onSave }: {
  domainId: string;
  initial: WAFRule;
  saving: boolean;
  onCancel: () => void;
  onSave: (r: { id?: number } & Omit<WAFRule, 'id'>) => void;
}) {
  const [f, setF] = useState<WAFRule>(initial);
  const set = <K extends keyof WAFRule>(k: K, v: WAFRule[K]) => setF((p) => ({ ...p, [k]: v }));
  const test = useTestWafExpression(domainId);

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent style={{ maxWidth: 640 }}>
        <DialogHeader><DialogTitle>{initial.id ? 'Edit WAF rule' : 'Add WAF rule'}</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <Field label="Rule ID" required>{(p) => <Input {...p} value={f.rule_id} onChange={(e) => set('rule_id', e.target.value)} disabled={!!initial.id} />}</Field>
              <Field label="Priority">{(p) => <Input {...p} type="number" value={String(f.priority)} onChange={(e) => set('priority', Number(e.target.value) || 0)} />}</Field>
              <Field label="Action">
                {(p) => (
                  <Select value={f.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>{WAF_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              {f.action === 'block' && <Field label="Block status">{(p) => <Input {...p} type="number" value={String(f.block_status)} onChange={(e) => set('block_status', Number(e.target.value) || 0)} />}</Field>}
            </div>
            <Field label="Expression" description='Wirefilter, e.g. http.uri.path contains "/admin"'>
              {(p) => <Textarea {...p} rows={3} value={f.expression} onChange={(e) => set('expression', e.target.value)} style={{ fontFamily: 'var(--vn-font-mono)', fontSize: 'var(--vn-font-size-sm)' }} />}
            </Field>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
              <Button variant="secondary" size="sm" startIcon={<FlaskConical size={14} />} disabled={!f.expression.trim() || test.isPending} onClick={() => test.mutate(f.expression)}>
                {test.isPending ? 'Testing…' : 'Test expression'}
              </Button>
              {test.data && (
                <span style={{ fontSize: 'var(--vn-font-size-sm)', color: test.data.valid ? 'var(--vn-success-text)' : 'var(--vn-danger-text)' }}>
                  {test.data.valid ? '✓ Valid expression' : `✗ ${test.data.error || 'Invalid expression'}`}
                </span>
              )}
            </div>
            <SettingRow title="Enabled" control={<Switch checked={f.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!f.rule_id.trim() || !f.expression.trim() || saving}
            onClick={() => onSave(f.id ? f : { ...f, id: undefined })}
          >
            {saving ? 'Saving…' : 'Save rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
